import { environment } from '../../../environments/environment';
import { AlcmeonContextService } from '../alcmeon-context/alcmeon-context.service';
import { CordovaService } from '../cordova.service';

declare const ALC_WEBPACK_BUILD_TIMESTAMP: string;
const TRACK_INTERVAL_MS = 30000;
const XHR_TIMEOUT_MS = 20000;

export class TrackClient {
  sessionId: string;

  private pendingEvents = [];
  private nextEventId = 0;
  private xhrId = 0;
  private isFlushing = false;
  private gzipWorker: Worker;
  private isDisabledBecauseLowBandwidth = false;
  private isManuallyDisabled = false;

  constructor(private cordovaService: CordovaService, private alcmeonContext: AlcmeonContextService) {
    this.sessionId = Math.random().toString(36).substring(2, 15);
    window.setInterval(() => this.maybeFlush(), TRACK_INTERVAL_MS);
    window.addEventListener('beforeunload', () => this.onWindowUnload(), false);
    window.addEventListener('unload', () => this.onWindowUnload(), false);
    if (window.Worker) {
      this.gzipWorker = new Worker('assets/js/gzip-worker.js');
      this.gzipWorker.onmessage = event => this.sendXHR(event.data, true);
    }
    this.isManuallyDisabled = window.location.href.includes('disable-track');
  }

  pushPending(type, event) {
    const clientType = environment.bundledWithAngularJSApp ? 'desktop' : environment.isNativeApp ? 'native' : 'mobile';
    const entry = {
      type,
      event,
      loggued_user: this.alcmeonContext.self ? this.alcmeonContext.self.id : 0,
      company: this.alcmeonContext.companyId || 0,
      client_session_id: this.sessionId,
      server_session_id: this.alcmeonContext.self ? this.alcmeonContext.self.session_id : 0,
      client_version: environment.ajsClientVersion,
      client_type: clientType,
      client_hash: this.clientBuildTimestamp(),
      stat_counter: this.nextEventId++,
      browser_time: Date.now(),
    };
    this.pendingEvents.push(entry);
  }

  pushOrAggregatePending(type, subEvent) {
    const lastPushedEvent = this.lastPushedEvent();
    if (lastPushedEvent?.type === type) {
      lastPushedEvent.event.events.push(subEvent);
    } else {
      this.pushPending(type, {
        events: [subEvent],
      });
    }
  }

  pushOrOverwritePending(type, event) {
    const lastPushedEvent = this.lastPushedEvent();
    if (lastPushedEvent?.type === type) {
      lastPushedEvent.event = event;
    } else {
      this.pushPending(type, event);
    }
  }

  private lastPushedEvent() {
    return this.pendingEvents.length > 0 ? this.pendingEvents[this.pendingEvents.length - 1] : null;
  }

  private maybeFlush() {
    try {
      if (environment.isTrackActivated && !this.isDisabledBecauseLowBandwidth && !this.isManuallyDisabled) {
        this.flush();
      } else {
        this.pendingEvents = [];
      }
    } catch (error) {
      console.error('error while flushing stats', error);
    }
  }

  private flush() {
    if (this.pendingEvents.length > 0 && !this.isFlushing) {
      this.isFlushing = true;
      const stringifiedEvents = JSON.stringify(this.pendingEvents);
      if (stringifiedEvents.length > 1000 && this.gzipWorker) {
        this.gzipWorker.postMessage(stringifiedEvents);
      } else {
        this.sendXHR(stringifiedEvents, false);
      }
    }
  }

  private onXhrTimeout() {
    console.warn(`TrackClient - timeout, disabling track`);
    this.isDisabledBecauseLowBandwidth = true;
  }

  private sendXHR(payload: string | Uint8Array, isCompressed) {
    const req = new XMLHttpRequest();
    req.timeout = XHR_TIMEOUT_MS;
    req.open('POST', this.endpointURL(), true);
    if (isCompressed) {
      req.setRequestHeader('Content-Encoding', 'gzip');
    }
    req.setRequestHeader('Content-Type', 'application/json');
    req.setRequestHeader('X-Alcmeon-Client-Hash', this.clientBuildTimestamp());
    req.setRequestHeader('X-Alcmeon-Request-Id', String(this.xhrId++));
    req.addEventListener('loadend', event => this.flushCompleted(event));
    req.addEventListener('timeout', () => this.onXhrTimeout());
    req.send(payload);
  }

  private flushCompleted(event: ProgressEvent) {
    this.isFlushing = false;
    if (event && event.target && (event.target as XMLHttpRequest).status === 200) {
      this.pendingEvents = [];
    }
  }

  private onWindowUnload() {
    if (!this.isFlushing) {
      this.isFlushing = true;
      this.pushPending('window-unload', {});
      const jsonString = JSON.stringify(this.pendingEvents);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(this.endpointURL(), jsonString);
      } else {
        // resort to a synchronous xhr if sendBeacon is not present
        const req = new XMLHttpRequest();
        req.open('POST', this.endpointURL(), false); // third parameter indicates sync xhr
        req.setRequestHeader('Content-Type', 'text/plain;charset=UTF-8');
        req.send(jsonString);
      }
    }
  }

  private endpointURL() {
    return `${environment.trackURLPrefix}/${this.sessionId}`;
  }

  private clientBuildTimestamp() {
    return environment.bundledWithAngularJSApp ? ALC_WEBPACK_BUILD_TIMESTAMP : `${this.cordovaService.appBuildNumber}`;
  }
}
